import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import { arrayOf, func, shape } from 'prop-types';
import { format, parseISO } from 'date-fns';
import { formatDateForExport } from 'utils/helpers/format';
import Tooltip from '@mui/material/Tooltip';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { ButtonGroup, MenuItem, Typography } from '@mui/material';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { batchStatusTooltips, isEditableStatus } from '../utils';
import StatusChip from '../components/StatusChip';

const BatchesGridView = ({ batches, onCancel, onCopy }) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);

  const handleEditClick = params => {
    navigate(`./${params.id}/edit`);
  };
  const handleViewClick = params => {
    navigate(`./${params.id}/edit`);
  };

  const isBatchOwnerAndManager = batchOwnerName =>
    R.and(
      affiliates.some(({ customerNameShort }) => R.equals(customerNameShort, batchOwnerName)),
      isProductShippingManage
    );

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'batchId',
      flex: 1,
      headerName: 'Batch Id',
      renderCell: ({ row }) => (
        <Typography
          className="heap-batch-id-link"
          onClick={() => navigate(`../batches/batch/${R.prop('batchId', row)}/edit`)}
          sx={{
            color: 'green',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          variant="text"
        >
          {R.prop('batchId', row)}
        </Typography>
      ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'batchOwnerName',
      flex: 1,
      headerName: 'Batch Owner',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      flex: 1,
      headerName: 'Status',
      renderCell: ({ row }) =>
        R.prop('status', row) && (
          <Tooltip placement="top-start" title={batchStatusTooltips[R.prop('status', row)]}>
            <StatusChip status={R.prop('status', row)} />
          </Tooltip>
        ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'acceptance',
      flex: 1,
      headerName: 'Acceptance',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'originName',
      flex: 1,
      headerName: 'Origin',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'productCode',
      flex: 1,
      headerName: 'Product Code',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'volume',
      flex: 1,
      headerName: 'Volume (bbls)',
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'accessCode',
      flex: 1,
      headerName: 'Access Code',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'availableDate',
      flex: 1,
      headerName: 'Available Date',
      renderCell: ({ row }) =>
        R.prop('availableDate', row)
          ? format(parseISO(R.prop('availableDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'action',
      flex: 1,
      headerName: 'Action',
      renderCell: params =>
        R.prop('batchId', params.row) && (
          <Box>
            {isBatchOwnerAndManager(params.row.batchOwnerName) &&
            isEditableStatus(params.row.status) ? (
              <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                <Button onClick={() => handleEditClick(params)}>Edit</Button>
                <MDropdownMenuButton>
                  <MenuItem onClick={() => onCopy('batch', params.row.batchId)}>
                    Copy Batch
                  </MenuItem>
                  <MenuItem onClick={() => onCancel('batch', params.row.batchId)}>
                    Cancel Batch
                  </MenuItem>
                </MDropdownMenuButton>
              </ButtonGroup>
            ) : !isEditableStatus(params.row.status) &&
              isBatchOwnerAndManager(params.row.batchOwnerName) ? (
              <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                <Button onClick={() => handleViewClick(params)}>View</Button>
                <MDropdownMenuButton>
                  <MenuItem onClick={() => onCopy('batch', params.row.batchId)}>
                    Copy Batch
                  </MenuItem>
                </MDropdownMenuButton>
              </ButtonGroup>
            ) : (
              <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                <Button onClick={() => handleViewClick(params)}>View</Button>
              </ButtonGroup>
            )}
          </Box>
        ),
    },
  ];

  return (
    <PersistentDataGrid
      apiRef={apiRef}
      cellSelection
      columns={columns}
      getRowId={R.prop('batchId')}
      hideFooter
      id="batches"
      initialState={{
        aggregation: {
          model: {
            volume: 'sum',
          },
        },
        pinnedColumns: {
          left: [],
          right: ['action'],
        },
      }}
      pageSize={10}
      quickFilter
      rows={batches}
      showFilterButton
    />
  );
};
BatchesGridView.propTypes = {
  batches: arrayOf(shape({})).isRequired,
  onCancel: func.isRequired,
  onCopy: func.isRequired,
};
export default BatchesGridView;
